
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */

import DocsBeta from 'src/components/docs/DocsBeta/index.tsx';

export const metadata = {
  toc: true,
  maxTocLevel: 4,
  layout: 'guide',
  section: 'Signal',
  title: 'Initialize Plaid Items',
  description: 'Create Plaid Items to be used with Signal Transaction Scores'
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
      console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope")
      return <div {...props}/>
    };
const Header = makeShortcode("Header");
const layoutProps = {
  metadata
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">

    <Header title={metadata.title} subtitle={metadata.description} mdxType="Header" />
    <h4 {...{
      "id": "creating-plaid-items"
    }}>{`Creating Plaid Items`}</h4>
    <p>{`Before getting a Signal Transaction Score for a proposed transaction, your end users need to link a bank account to your app using `}<a parentName="p" {...{
        "href": "/docs/link"
      }}>{`Link`}</a>{`, Plaid's client-side widget. Link will connect the user's bank account and obtain the consent required to perform this evaluation.`}</p>
    <p>{`See the `}<a parentName="p" {...{
        "href": "/docs/link"
      }}>{`Link documentation`}</a>{` for more details on setting up a Plaid Link session. At a high level, the steps are:`}</p>
    <ol>
      <li parentName="ol">
        <p parentName="li">{`Call `}<inlineCode parentName="p">{`/link/token/create`}</inlineCode>{`.`}</p>
        <ul parentName="li">
          <li parentName="ul">{`Put `}<inlineCode parentName="li">{`signal`}</inlineCode>{` and `}<inlineCode parentName="li">{`auth`}</inlineCode>{` in the `}<inlineCode parentName="li">{`products`}</inlineCode>{` array, along with any other Plaid products (except Balance) you will be requiring, e.g. `}<inlineCode parentName="li">{`products: [signal, auth]`}</inlineCode>{`.`}</li>
          <li parentName="ul">{`Put any other Plaid products you plan to use in the `}<inlineCode parentName="li">{`optional_products`}</inlineCode>{` or `}<inlineCode parentName="li">{`required_if_supported_products`}</inlineCode>{` arrays, e.g. `}<inlineCode parentName="li">{`required_if_supported_products: [identity]`}</inlineCode>{`.`}</li>
        </ul>
      </li>
      <li parentName="ol">
        <p parentName="li">{`Initialize Link using the `}<inlineCode parentName="p">{`link_token`}</inlineCode>{` created in the previous step. For more details for your specific platform, see the `}<a parentName="p" {...{
            "href": "/docs/link"
          }}>{`Link documentation`}</a>{`. The user will now go through the Link flow.`}</p>
      </li>
      <li parentName="ol">
        <p parentName="li">{`Call `}<inlineCode parentName="p">{`/item/public_token/exchange`}</inlineCode>{` to exchange the `}<inlineCode parentName="p">{`public_token`}</inlineCode>{` for an `}<inlineCode parentName="p">{`access_token`}</inlineCode>{`. `}</p>
      </li>
      <li parentName="ol">
        <p parentName="li">{`Obtain the `}<inlineCode parentName="p">{`account_id`}</inlineCode>{` of the account used for the transaction you wish to perform the evaluation on; this can be obtained from the `}<inlineCode parentName="p">{`metadata.accounts`}</inlineCode>{` field in the `}<inlineCode parentName="p">{`onSuccess`}</inlineCode>{` callback, or by calling `}<inlineCode parentName="p">{`/accounts/get`}</inlineCode>{` or `}<inlineCode parentName="p">{`/link/token/get`}</inlineCode>{`.`}</p>
      </li>
    </ol>
    <p>{`Once you have your Plaid Item, continue to `}<a parentName="p" {...{
        "href": "/docs/signal/signal-rules"
      }}>{`evaluate`}</a>{` the risk of the transaction.`}</p>
    <h4 {...{
      "id": "adding-signal-to-existing-items"
    }}>{`Adding Signal to existing Items`}</h4>
    <p>{`You may have Items that were not initialized with `}<inlineCode parentName="p">{`signal`}</inlineCode>{`; for example, if you are adding Signal support to an existing Plaid integration.`}</p>
    <p>{`If you intend to add Signal Transaction Scores to an existing Item, and the user has not already agreed to the risk product disclosure in Link, you will receive the `}<a parentName="p" {...{
        "href": "/docs/errors/invalid-input/#additional_consent_required"
      }}><inlineCode parentName="a">{`ADDITIONAL_CONSENT_REQUIRED`}</inlineCode></a>{` error when calling `}<inlineCode parentName="p">{`/signal/evaluate`}</inlineCode>{`. As of June 2026, the user must agree to this disclosure before you can call `}<inlineCode parentName="p">{`/signal/evaluate`}</inlineCode>{` with a Signal Transaction Scores–powered ruleset, even if you have not enabled `}<a parentName="p" {...{
        "href": "/docs/link/data-transparency-messaging-migration-guide/"
      }}>{`Data Transparency Messaging`}</a>{`. To resolve this error, send the Item through `}<a parentName="p" {...{
        "href": "/docs/link/update-mode/#requesting-additional-consented-products"
      }}>{`update mode`}</a>{`, making sure to include `}<inlineCode parentName="p">{`signal`}</inlineCode>{` in the `}<inlineCode parentName="p">{`additional_consented_products`}</inlineCode>{` field when calling `}<inlineCode parentName="p">{`/link/token/create`}</inlineCode>{`. Evaluating a balance with a Balance-only ruleset does not require this disclosure; for more details, see `}<a parentName="p" {...{
        "href": "/docs/balance/#adding-balance-to-existing-items"
      }}>{`Adding Balance to existing Items`}</a>{`. `}</p>
    <p>{`The risk product disclosure will automatically be presented in the Link flow if `}<inlineCode parentName="p">{`signal`}</inlineCode>{` is included in any of the products arrays, including `}<inlineCode parentName="p">{`products`}</inlineCode>{`, `}<inlineCode parentName="p">{`additional_consented_products`}</inlineCode>{`, `}<inlineCode parentName="p">{`optional_products`}</inlineCode>{`, or `}<inlineCode parentName="p">{`required_if_supported_products`}</inlineCode>{`, in the `}<inlineCode parentName="p">{`/link/token/create`}</inlineCode>{` call used to create the Link token. `}</p>
    <p>{`For Items that have already consented to Signal where Signal was not extracted (for example, `}<inlineCode parentName="p">{`signal`}</inlineCode>{` was passed into `}<inlineCode parentName="p">{`additional_consented_products`}</inlineCode>{`), you can optionally call `}<inlineCode parentName="p">{`/signal/prepare`}</inlineCode>{` on the Item before your first call to `}<inlineCode parentName="p">{`/signal/evaluate`}</inlineCode>{` to pre-load scoring data and improve the latency and accuracy of the call to `}<inlineCode parentName="p">{`/signal/evaluate`}</inlineCode>{`.`}</p>
    <p>{`If you have a large number of existing Items that require update mode to add consent, contact your Plaid account manager for more details and assistance. `}</p>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;

            export const tableOfContents = [
      {
        id: "creating-plaid-items",
        level: 4,
        title: "Creating Plaid Items",
      },
{
        id: "adding-signal-to-existing-items",
        level: 4,
        title: "Adding Signal to existing Items",
      }
];
          
layoutProps.tableOfContents = tableOfContents;
MDXContent.layoutProps = layoutProps;

MDXContent.layout = function (props) { return <DocsBeta {...props} /> };
